// ============================================================
// The template previews, memoized - see areaPreviews.js for the rules
// ============================================================

import { useMemo } from 'react';
import {
  templateOf,
  blastCentreOf,
  caughtInBlast,
  aimFrom,
  caughtInCone,
  caughtAlongBeam,
  arcPreviewOf,
  spawnPreviewOf,
  overwatchZonesOf,
  namesOf,
  markersOf,
} from './areaPreviews';
import { EMPTY } from './panelDefaults';
import { hordeMagnitudes } from '../../engine/roster/hordes';

/** The names and the markers of a list of caught ids, for an overlay's label and rings. */
function useCaught(ids, names, tokens) {
  const caught = useMemo(() => namesOf(ids, names), [ids, names]);
  const caughtAt = useMemo(() => markersOf(ids, tokens), [ids, tokens]);
  return { ids, caught, caughtAt };
}

function useBlast(blastRadius, { line, targetId, tokens, tokenList, sight, names }) {
  const centre = useMemo(
    () => blastCentreOf(blastRadius, line, targetId, tokens),
    [blastRadius, line, targetId, tokens],
  );
  const ids = useMemo(
    () => caughtInBlast(centre, blastRadius, tokenList, sight),
    [centre, blastRadius, tokenList, sight],
  );
  return { centre, radius: blastRadius, ...useCaught(ids, names, tokens) };
}

function useCone(degrees, reach, { shooter, line, targetId, tokens, tokenList, sight, names }) {
  const aim = useMemo(
    () => (degrees > 0 && shooter ? aimFrom(shooter, line, targetId, tokens, reach) : null),
    [degrees, shooter, line, targetId, tokens, reach],
  );
  const ids = useMemo(() => caughtInCone(aim, degrees, tokenList, sight), [aim, degrees, tokenList, sight]);
  return { aim, degrees, ...useCaught(ids, names, tokens) };
}

function useBeam(active, reach, halfWidth, { shooter, line, targetId, tokens, tokenList, sight, names }) {
  const aim = useMemo(
    () => (active && shooter ? aimFrom(shooter, line, targetId, tokens, reach) : null),
    [active, shooter, line, targetId, tokens, reach],
  );
  const ids = useMemo(() => caughtAlongBeam(aim, halfWidth, tokenList, sight), [aim, halfWidth, tokenList, sight]);
  return { aim, halfWidth, ...useCaught(ids, names, tokens) };
}

function useArc(area, { targetId, tokens, tokenList, sight, names }) {
  const primary = targetId ? tokens[targetId] : null;
  const preview = useMemo(() => arcPreviewOf(area, primary, tokenList, sight), [area, primary, tokenList, sight]);
  const caught = useMemo(() => (preview ? namesOf(preview.ids, names) : EMPTY), [preview, names]);
  const chainAt = useMemo(
    () =>
      preview
        ? markersOf(
            preview.chain.map((c) => c.id),
            tokens,
          )
        : EMPTY,
    [preview, tokens],
  );
  return { preview, caught, chainAt };
}

/** The spawned template, from whoever the primary is predicted to catch (v8.373.5). */
function useSpawn({
  area,
  arcPreview,
  blastIds,
  coneIds,
  beamIds,
  targetId,
  shooter,
  tokenList,
  tokens,
  sight,
  spawnEligible,
}) {
  return useMemo(
    () =>
      spawnPreviewOf(
        area,
        { blast: blastIds, cone: coneIds, beam: beamIds, arc: arcPreview },
        { targetId, shooter, tokenList, tokens, sight, spawnEligible },
      ),
    [area, arcPreview, blastIds, coneIds, beamIds, targetId, shooter, tokenList, tokens, sight, spawnEligible],
  );
}

/**
 * Every template the selected weapon would lay, and who each would catch -
 * plus the declared overwatch zones, which are drawn beside them.
 *
 * `inputs`: the panel's props (area, the legacy template props, the
 * eligibility predicates, roster, actor and target, weaponRangeM) and the
 * map's live state (tokens, tokenList, names, solids, the tape's `line`).
 */
export function useAreaPreviews(inputs) {
  const { area, tokens, actorId, roster, solids, ignoreTerrain, isGm, areaEligible, relationRules } = inputs;
  const template = templateOf(inputs);
  // A horde's bulk: the shell's record when there is one; the asset editors' standalone panel reads the roster.
  const hordeMagnitude = useMemo(
    () => relationRules?.hordeMagnitude ?? hordeMagnitudes(roster ?? []),
    [relationRules, roster],
  );
  const sight = useMemo(
    () => ({ ignoreTerrain, solids, isGm, hordeMagnitude, actorId, eligible: areaEligible }),
    [ignoreTerrain, solids, isGm, hordeMagnitude, actorId, areaEligible],
  );
  const shared = { ...inputs, sight, shooter: actorId ? tokens[actorId] : null };
  const blast = useBlast(template.blastRadius, shared);
  const cone = useCone(template.sprayDegrees, template.templateReach, shared);
  const beam = useBeam(template.beamActive, template.templateReach, template.beamHalfWidth, shared);
  const arc = useArc(area, shared);
  const spawn = useSpawn({
    ...shared,
    arcPreview: arc.preview,
    blastIds: blast.ids,
    coneIds: cone.ids,
    beamIds: beam.ids,
    spawnEligible: inputs.areaSpawnEligible,
  });
  const overwatch = useMemo(
    () => overwatchZonesOf(roster, tokens, inputs.weaponRangeM),
    [roster, tokens, inputs.weaponRangeM],
  );
  return { blast, cone, beam, arc, spawn, overwatch };
}
